import * as cn from 'classnames'
import { Component } from 'react'
import * as sortBy from 'lodash/sortBy'
import { MoneyflowBreakdownCollection, MoneyflowTrackersCollection, MoneyflowBreakdownOwnProps, MoneyflowTracker } from 'interfaces/moneyflow'
import { CollectionCategory } from 'interfaces/meta'
import { Timeframe } from 'interfaces/chart'
import { PeergroupRelation } from 'interfaces/peergroups'
import * as styles from 'styles/organisms/modules/Breakdown.css'
import Button from 'components/features/pfm/Button/Button'
import PeergroupLegend from 'components/features/pfm/PeergroupLegend/PeergroupLegend'
import { getItemsFromCollection, getItemsByKey } from 'helpers/normalizeHelpers'
import { CategoryId } from 'interfaces/meta'
import { getMax } from 'helpers/formatHelpers'
import { getMonthsCount } from 'helpers/dateTimeHelpers'
import { ALL_CATEGORY_ID, TRANSFER_CATEGORY_ID } from 'constants/meta'
import MoneyflowBreakdownItem from 'components/molecules/MoneyflowBreakdownItem'
import * as isNumber from 'lodash/isNumber'

export type MoneyflowBreakdownStateProps = {
  isExpanded: boolean
  selectedCategoryId: CategoryId
  trackers: MoneyflowTrackersCollection | null
  timeframe: Timeframe
  categories: CollectionCategory
  memberValues: MoneyflowBreakdownCollection
  peerValues: MoneyflowBreakdownCollection
  otherValues: MoneyflowBreakdownCollection
  selectedRelation: PeergroupRelation
  withComparison: boolean
}

export type MoneyflowBreakdownDispatchProps = {
  setExpanded: (isExpanded: boolean) => void
  setExpandedCategory: (categoryId: CategoryId) => void
  focusLimitField: () => void
}

export type MoneyflowBreakdownProps =
  & MoneyflowBreakdownStateProps
  & MoneyflowBreakdownDispatchProps
  & MoneyflowBreakdownOwnProps

// TODO @pmyagkov merge moneyflow and account breakdowns
class MoneyflowBreakdown extends Component<MoneyflowBreakdownProps> {
  private pageYOffset: number | null = null

  private get notAllCategorySelected () {
    const { selectedCategoryId } = this.props

    return selectedCategoryId !== ALL_CATEGORY_ID
  }

  render () {
    const {
      memberValues,
      peerValues,
      otherValues,
      categories,
      timeframe,
      trackers,
      type,
      isExpanded,
      focusLimitField,
      setExpandedCategory,
      withComparison,
    } = this.props

    const {
      amount: memberTotalAmount,
      projected: memberTotalProjected,
    } = memberValues.byKey[ALL_CATEGORY_ID]
    const {
      amount: peerTotalAmount,
      projected: peerTotalProjected,
    } = peerValues.byKey[ALL_CATEGORY_ID]
    const {
      amount: otherTotalAmount,
      projected: otherTotalProjected,
    } = otherValues.byKey[ALL_CATEGORY_ID]

    const monthsCount = getMonthsCount(timeframe)
    const calibratedTrackerLimits = trackers ? getItemsFromCollection<MoneyflowTracker, 'category'>(trackers).map(({ limit }) => limit * monthsCount) : []

    const maxLimit = getMax(calibratedTrackerLimits)
    const maxAmount = getMax([
      memberTotalAmount,
      withComparison ? peerTotalAmount : null,
      withComparison ? otherTotalAmount : null,
      maxLimit,
    ])
    const maxProjected = getMax([
      memberTotalProjected,
      withComparison ? peerTotalProjected : null,
      withComparison ? otherTotalProjected : null,
    ])
    const maxValue = getMax([maxAmount, maxProjected])

    const memberItems = this.getMemberItemsSorted()
    const memberItemsToRender = isExpanded
      ? memberItems
      : memberItems.slice(0, 5)

    const showAllCategoriesButton = type === 'spending' && !this.notAllCategorySelected ? (
      <li className={ cn(styles.breakdown, styles.seeAllButtonRow) }>
        <Button
          fullWidth
          theme='lightBlue'
          size='small'
          onClick={ this.handleExpandButtonClick }
        >
          { isExpanded ? 'See less' : 'See All Categories' }
        </Button>
      </li>
    ) : null

    return (
      <>
        { withComparison && this.renderLegend() }
        <ul>
          {
            memberItemsToRender.map((memberItem) => (
              <MoneyflowBreakdownItem
                category={ categories.byKey[memberItem.category] }
                key={ memberItem.category }
                type={ type }

                memberItem={ memberItem }
                peerItem={ peerValues.byKey[memberItem.category] }
                otherItem={ otherValues.byKey[memberItem.category] }
                tracker={ trackers ? trackers.byKey[memberItem.category] : undefined }

                maxValue={ maxValue }
                monthsCount={ monthsCount }

                focusLimitField={ focusLimitField }
                setExpandedCategory={ setExpandedCategory }

                timeframe={ timeframe }
                withComparison={ withComparison }
              />
            ))
          }
          { showAllCategoriesButton }
        </ul>
      </>
    )

  }

  renderLegend () {
    const { selectedRelation } = this.props

    return (
      <div className={ styles.legendWrapper }>
        <PeergroupLegend othersName={ selectedRelation.name } />
      </div>
    )
  }

  private getMemberItemsSorted () {
    const {
      memberValues,
      categories,
      selectedCategoryId,
    } = this.props

    const [allCategoryBreakdown, ...restBreakdowns] = this.notAllCategorySelected
      ? getItemsByKey(memberValues.byKey, [ALL_CATEGORY_ID, selectedCategoryId])
      : getItemsFromCollection(memberValues)

    const filteredBreakdowns = restBreakdowns.filter(({ category }) => category !== TRANSFER_CATEGORY_ID)
    const restBreakdownSorted = sortBy(
      filteredBreakdowns,
      (breakdown) => -breakdown.amount,
      (breakdown) => isNumber(breakdown.projected) ? -breakdown.projected : 0,
      (breakdown) => categories.allKeys.indexOf(breakdown.category)
    )

    return [allCategoryBreakdown].concat(restBreakdownSorted)
  }

  private handleExpandButtonClick = () => {
    const { isExpanded, setExpanded } = this.props

    setExpanded(!isExpanded)

    if (!isExpanded) {
      this.pageYOffset = window.pageYOffset
    } else if (this.pageYOffset !== null) {
      window.scrollTo(window.pageXOffset, this.pageYOffset)
    }
  }
}

export default MoneyflowBreakdown
